import React, { useState, useEffect } from "react";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import usePagination from "../hooks/usePagination";
import Pagination from "./Pagination";

// Leads Management Component - Extracted from AdminDashboard for reusability
const LeadsManagement = ({ userRole = "admin" }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    qualified: 0,
    converted: 0,
  });

  // Use pagination hook
  const {
    data: leads,
    pagination,
    loading,
    error,
    setPage,
    setLimit,
    setSearch,
    setFilters,
    refresh,
    searchValue,
  } = usePagination("/api/leads", {
    initialLimit: 20,
    defaultSortBy: "created_at",
    defaultSortOrder: "DESC",
  });

  useEffect(() => {
    fetchStats();
  }, []); // Only fetch stats once on mount

  // Update filters when status or source changes
  useEffect(() => {
    const filters = {};
    if (statusFilter) filters.status = statusFilter;
    if (sourceFilter) filters.source = sourceFilter;
    setFilters(filters);
  }, [statusFilter, sourceFilter, setFilters]);

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/analytics/overview");
      const analyticsData = response.data;
      setStats({
        total: analyticsData.leads?.total_leads || 0,
        new: analyticsData.leads?.new_leads || 0,
        contacted: analyticsData.leads?.contacted_leads || 0,
        qualified: analyticsData.leads?.qualified_leads || 0,
        converted: analyticsData.leads?.converted_leads || 0,
      });
    } catch (error) {
      console.error("Error fetching lead stats:", error);
      // Set default stats on error
      setStats({
        total: 0,
        new: 0,
        contacted: 0,
        qualified: 0,
        converted: 0,
      });
    }
  };

  const updateLeadStatus = async (leadId, newStatus, notes = "") => {
    try {
      await axios.put(`/api/leads/${leadId}`, {
        status: newStatus,
        notes: notes,
      });
      toast.success("Lead status updated successfully");
      refresh();
      fetchStats();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead status");
    }
  };

  const viewLeadDetails = async (leadId) => {
    try {
      const response = await axios.get(`/api/leads/${leadId}`);
      setSelectedLead(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error fetching lead details:", error);
      toast.error("Failed to fetch lead details");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      await axios.delete(`/api/leads/${leadId}`);
      toast.success("Lead deleted successfully");
      refresh();
      fetchStats();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "contacted":
        return "bg-yellow-100 text-yellow-800";
      case "qualified":
        return "bg-purple-100 text-purple-800";
      case "converted":
        return "bg-green-100 text-green-800";
      case "lost":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leads Management</h1>
        <button
          onClick={refresh}
          className="btn-secondary btn-md flex items-center gap-2"
        >
          <span>🔄</span> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-500">
          <p className="text-sm text-gray-600">Total Leads</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">New</p>
          <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <p className="text-sm text-gray-600">Contacted</p>
          <p className="text-2xl font-bold text-yellow-600">
            {stats.contacted}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
          <p className="text-sm text-gray-600">Qualified</p>
          <p className="text-2xl font-bold text-purple-600">
            {stats.qualified}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-600">Converted</p>
          <p className="text-2xl font-bold text-green-600">{stats.converted}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sources</option>
              <option value="website">Website</option>
              <option value="social_media">Social Media</option>
              <option value="referral">Referral</option>
              <option value="campaign">Campaign</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter("");
                setSourceFilter("");
                setSearch("");
              }}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="loading-dots">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Error: {error}</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No leads found matching the filters.
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Lead Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {lead.name}
                      </div>
                      <div className="text-sm text-gray-500">ID: {lead.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{lead.email}</div>
                      <div className="text-sm text-gray-500">{lead.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.source || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={lead.status}
                        onChange={(e) =>
                          updateLeadStatus(lead.id, e.target.value)
                        }
                        className={`text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                          lead.status
                        )}`}
                        disabled={
                          userRole === "telecaller" &&
                          lead.status === "converted"
                        }
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        {(userRole === "admin" || userRole === "sales") && (
                          <option value="converted">Converted</option>
                        )}
                        <option value="lost">Lost</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => viewLeadDetails(lead.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      {userRole === "admin" && (
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Component */}
            <div className="px-6 py-4 border-t border-gray-200">
              <Pagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={setLimit}
                onSearchChange={setSearch}
                searchValue={searchValue}
                searchPlaceholder="Search by name, email, phone, or WhatsApp..."
                showSearch={true}
                showLimitSelector={true}
                limitOptions={[10, 20, 50, 100]}
              />
            </div>
          </>
        )}
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Lead Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Contact Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Name</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.phone}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">WhatsApp</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.whatsapp}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Source</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.source}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <p
                      className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                        selectedLead.lead.status
                      )}`}
                    >
                      {selectedLead.lead.status.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedLead.lead.notes && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Notes</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedLead.lead.notes}
                  </p>
                </div>
              )}

              {/* Appointments */}
              {selectedLead.appointments &&
                selectedLead.appointments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Appointments ({selectedLead.appointments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedLead.appointments.map((apt) => (
                        <div
                          key={apt.id}
                          className="bg-gray-50 p-3 rounded flex justify-between items-center"
                        >
                          <div>
                            <p className="font-medium">
                              {new Date(apt.scheduled_at).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              Status: {apt.status}
                            </p>
                          </div>
                          {apt.meeting_link && (
                            <a
                              href={apt.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Join Meeting
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Communications */}
              {selectedLead.communications &&
                selectedLead.communications.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Communications ({selectedLead.communications.length})
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedLead.communications.map((comm) => (
                        <div key={comm.id} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-semibold text-gray-600">
                              {comm.type.toUpperCase()} -{" "}
                              {comm.template_type.replace("_", " ")}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                comm.status === "sent"
                                  ? "bg-green-100 text-green-800"
                                  : comm.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {comm.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {comm.content}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(comm.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManagement;
